import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type WorkbenchVisibility = "PUBLICA" | "PRIVADA";
export type WorkbenchStatus = "ACTIVA" | "ARCHIVADA";
export type WorkbenchMemberRole = "PROPIETARIO" | "MIEMBRO";
export type WorkbenchFileOrigin = "SUBIDO" | "GENERADO";

export const WORKBENCH_VISIBILITY_LABEL: Record<WorkbenchVisibility, string> = {
  PUBLICA: "Pública",
  PRIVADA: "Privada",
};

export interface WorkbenchRecord {
  id: string;
  organizationId: string;
  name: string;
  description: string | null;
  visibility: WorkbenchVisibility;
  status: WorkbenchStatus;
  createdByUserId: string | null;
  createdAt: string;
  createdBy?: { id: string; fullName: string; email: string } | null;
}

export interface WorkbenchMemberRecord {
  id: string;
  workbenchId: string;
  userId: string;
  role: WorkbenchMemberRole;
  user?: { id: string; fullName: string; email: string };
}

export interface WorkbenchFileRecord {
  id: string;
  workbenchId: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  origin: WorkbenchFileOrigin;
  createdAt: string;
  uploadedBy?: { id: string; fullName: string; email: string } | null;
}

export interface DirectoryUser {
  id: string;
  fullName: string;
  email: string;
}

/** Directorio de personal interno de la organización, para elegir miembros de una mesa privada (no requiere users.read). */
export function useWorkbenchDirectory(search = "") {
  return useQuery({
    queryKey: ["workbenches", "directory", search],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<DirectoryUser[]>>("/workbenches/directory", {
        params: search ? { search } : {},
      });
      return data.data;
    },
  });
}

export interface ListWorkbenchesFilters {
  page?: number;
  pageSize?: number;
  visibility?: WorkbenchVisibility;
  status?: WorkbenchStatus;
  search?: string;
}

export function useWorkbenches(filters: ListWorkbenchesFilters = {}) {
  return useQuery({
    queryKey: ["workbenches", filters],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<WorkbenchRecord[]>>("/workbenches", { params: filters });
      return data;
    },
  });
}

export function useWorkbench(id: string | undefined) {
  return useQuery({
    queryKey: ["workbenches", id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<{ workbench: WorkbenchRecord; members: WorkbenchMemberRecord[] }>>(
        `/workbenches/${id}`
      );
      return data.data;
    },
  });
}

export interface CreateWorkbenchPayload {
  name: string;
  description?: string;
  visibility?: WorkbenchVisibility;
  memberUserIds?: string[];
}

export function useCreateWorkbench() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateWorkbenchPayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<WorkbenchRecord>>("/workbenches", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workbenches"] }),
  });
}

export function useUpdateWorkbench() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; name?: string; description?: string; visibility?: WorkbenchVisibility }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<WorkbenchRecord>>(`/workbenches/${id}`, payload);
      return data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workbenches"] });
      queryClient.invalidateQueries({ queryKey: ["workbenches", variables.id] });
    },
  });
}

export function useArchiveWorkbench() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<WorkbenchRecord>>(`/workbenches/${id}/archive`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workbenches"] }),
  });
}

export function useDeleteWorkbench() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/workbenches/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workbenches"] }),
  });
}

export function useAddWorkbenchMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workbenchId, userId }: { workbenchId: string; userId: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<WorkbenchMemberRecord>>(`/workbenches/${workbenchId}/members`, { userId });
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["workbenches", variables.workbenchId] }),
  });
}

export function useRemoveWorkbenchMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workbenchId, userId }: { workbenchId: string; userId: string }) => {
      await apiClient.delete(`/workbenches/${workbenchId}/members/${userId}`);
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["workbenches", variables.workbenchId] }),
  });
}

export function useWorkbenchFiles(workbenchId: string | undefined) {
  return useQuery({
    queryKey: ["workbenches", workbenchId, "files"],
    enabled: !!workbenchId,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<WorkbenchFileRecord[]>>(`/workbenches/${workbenchId}/files`);
      return data.data;
    },
  });
}

export function useUploadWorkbenchFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workbenchId, file }: { workbenchId: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post<ApiSuccessResponse<WorkbenchFileRecord>>(`/workbenches/${workbenchId}/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["workbenches", variables.workbenchId, "files"] }),
  });
}

/** Edición real "en el sistema" (continuación posterior a la Fase 26): sobrescribe el contenido real del mismo archivo. */
export function useUpdateWorkbenchFileContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workbenchId, fileId, blob, filename }: { workbenchId: string; fileId: string; blob: Blob; filename: string }) => {
      const formData = new FormData();
      formData.append("file", blob, filename);
      const { data } = await apiClient.put<ApiSuccessResponse<WorkbenchFileRecord>>(
        `/workbenches/${workbenchId}/files/${fileId}/content`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["workbenches", variables.workbenchId, "files"] }),
  });
}

/**
 * Descarga los bytes reales de un archivo de la mesa (para abrirlo en el
 * editor en el navegador). A diferencia de downloadWorkbenchFile (URL
 * firmada de S3/MinIO pensada para `window.open` en pestaña nueva), esto usa
 * el endpoint propio del backend GET /:id/files/:fileId/content: el editor
 * necesita los bytes en memoria (para exceljs/mammoth), lo que exige un
 * fetch() en la propia página, y una URL firmada de S3/MinIO no tiene CORS
 * habilitado para el origen del frontend. apiClient ya envía el bearer token
 * y el backend ya permite este origen en CORS_ORIGIN, así que este endpoint
 * funciona sin configuración adicional.
 */
export async function fetchWorkbenchFileBytes(workbenchId: string, fileId: string): Promise<ArrayBuffer> {
  const { data } = await apiClient.get<ArrayBuffer>(`/workbenches/${workbenchId}/files/${fileId}/content`, {
    responseType: "arraybuffer",
  });
  return data;
}

export function useGenerateBlankExcel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workbenchId, sheetName }: { workbenchId: string; sheetName?: string }) => {
      const { data } = await apiClient.post<ApiSuccessResponse<WorkbenchFileRecord>>(`/workbenches/${workbenchId}/files/generate-excel`, {
        sheetName,
      });
      return data.data;
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["workbenches", variables.workbenchId, "files"] }),
  });
}

export function useGenerateBlankWord() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (workbenchId: string) => {
      const { data } = await apiClient.post<ApiSuccessResponse<WorkbenchFileRecord>>(`/workbenches/${workbenchId}/files/generate-word`, {});
      return data.data;
    },
    onSuccess: (_data, workbenchId) => queryClient.invalidateQueries({ queryKey: ["workbenches", workbenchId, "files"] }),
  });
}

export function useDeleteWorkbenchFile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ workbenchId, fileId }: { workbenchId: string; fileId: string }) => {
      await apiClient.delete(`/workbenches/${workbenchId}/files/${fileId}`);
    },
    onSuccess: (_data, variables) => queryClient.invalidateQueries({ queryKey: ["workbenches", variables.workbenchId, "files"] }),
  });
}

/**
 * Obtiene una URL de descarga firmada (expira en minutos) para un archivo
 * de la mesa. Mismo patrón que downloadDocument en api/documents.ts.
 */
export async function downloadWorkbenchFile(workbenchId: string, fileId: string): Promise<{ url: string; originalName: string }> {
  const { data } = await apiClient.get<ApiSuccessResponse<{ url: string; originalName: string; mimeType: string }>>(
    `/workbenches/${workbenchId}/files/${fileId}/download`
  );
  return data.data;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
